import React, { Fragment, useEffect, useState, useRef } from "react";
import {
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  ReferenceDot,
  ComposedChart,
  Line,
} from "recharts";

import {
  MAIN_COLORS,
  CustomizedYAxisTick,
  StandardTooltip,
  selectFormat,
  Error,
  formatDateFromData,
  isDateFromDataInISOFormat,
  getAllKeys,
  CustomizedYAxisTickRight,
  CustomizedYAxisTickRightPercent,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsTickIndicator,
  RenderBackground,
  closeAllBarchartTooltips,
} from "../Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

import moment from "moment";
import { formatMetricKey } from "components/Utils/ChartsDataUtils.js";

const metricNames = {
  opens: "Unique Opens",
  clicks: "Unique Clicks",
  open_rate: "Unique Open Rate",
};

const LeeDailyTrendCampaignStackedBars = (props) => {
  const { data, title } = props;
  const [widgetsInsights, setWidgetsInsights] = useState([]);
  const [state, setState] = useState({
    width: 0,
    height: 0,
    tooltip: {
      show: false,
      x: 0,
      y: 0,
    },
  });
  const node = useRef();
  const divPosition = useRef();

  useEffect(() => {
    props.childRef({ getExportData: () => getExportData(props) });
    window.addEventListener("resize", updatePosition);
    window.addEventListener("click", handleClickOutside);
    setTimeout(() => {
      updatePosition();
    }, 500);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("click", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const updatePosition = () => {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();
  };

  const handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  const insightsData = widgetsInsights.length > 0;

  useEffect(() => {
    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              setWidgetsInsights(data);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clickBar = (d, dataKey) => {
    if (state.tooltip.show && d.date === state.tooltip.date) {
      setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from and other widgets as well
      setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  };

  const closeTooltip = () => {
    setState({
      tooltip: { show: false },
    });
  };

  const renderCharts = (data) => {
    let labels = [];
    const sortedKeys = getSortedKeys(data);
    const insights = widgetsInsights;

    const allCharts = [];
    sortedKeys.forEach((source, key) => {
      if (source.toLocaleLowerCase().includes("click")) {
        allCharts.push(
          <Line
            key={key}
            yAxisId="right"
            // stackId="stack"
            type="monotone"
            dataKey={source}
            stroke={MAIN_COLORS[key]}
            strokeWidth={1}
            dot={false}
          />
        );
      } else {
        allCharts.push(
          <Bar
            key={key}
            dataKey={source}
            stackId="stack"
            fill={MAIN_COLORS[key]}
            isAnimationActive={false}
            // only first bar needs background
            background={
              key === 0 ? (
                <RenderBackground
                  insights={insights}
                  showIndicator={!state.tooltip.show}
                  node={node}
                  i={key}
                />
              ) : null
            }
            onClick={(d) => {
              const label = labels.filter((l) => l.date === d.date);
              const extra = sortedKeys
                .map((k, j) => ({
                  key: k,
                  value: d[k],
                  color: MAIN_COLORS[j],
                }))
                .filter((o) => o.value !== undefined);

              d.extra = extra;
              d.insight = label.length ? label[0] : null;
              clickBar(d, source);
            }}
          />
        );
      }
    });
    return allCharts;
  };

  const legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="legend mt-2 mt-md-0">
        <span
          className="legend__icon"
          style={{ background: MAIN_COLORS[i] }}
        ></span>
        <span className="legend__text" style={{ color: MAIN_COLORS[i] }}>
          {metricNames[entry] || formatMetricKey(entry)}
        </span>
      </span>
    ));

  const renderLegends = (data) => {
    let legends = [];
    let all_legends = [];

    const sortedKeys = getSortedKeys(data);

    for (let i = 0, j = data.length; i <= j - 1; i++) {
      sortedKeys.forEach((source) => all_legends.push(source));
    }

    legends = [...new Set(all_legends)];
    return legendElement(legends);
  };

  const getSortedKeys = (_data) => {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, ["date", "full_date"]);
    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  };

  const structureData = (d) => {
    ["Non-Subscriber", "Subscriber"].forEach((metric) => {
      if (d[metric]) {
        Object.entries(d[metric]).forEach(([key, value]) => {
          d[metric + " " + key] = value;
        });

        delete d[metric];
      }
    });

    if (props.url.includes("customer-market-overview")) {
      delete d.market;
    }
    return d;
  };

  const getExportData = (props) => {
    const isInDateFormat =
      props.data.data &&
      props.data.data.length > 0 &&
      props.data.data[0].date &&
      moment(props.data.data[0].date, "YYYY-MM-DD").isValid();
    return {
      data: props.data.data.map(structureData),
      opts: {
        type: "chart",
        date: {
          // overwrites the default settings for date only when the date is in ISO format
          defined: isDateFromDataInISOFormat(props.data.data) || isInDateFormat,
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  if (data.hasData === false) {
    return <Error minHeight={360} />;
  }

  if (!props.data || !props.data.data || props.data.data.length === 0) {
    return [];
  }

  let preparedData = formatDateFromData(props.data.data, "YYYY/MM/DD").map(
    structureData
  );

  const allKeys = getSortedKeys(preparedData);

  return (
    <Fragment>
      <div ref={divPosition}>
        {renderLegends(preparedData)}
        <div className="d-flex mt-2">
          <ResponsiveContainer height={330}>
            <ComposedChart
              data={preparedData}
              stackOffset="sign"
              margin={{ top: 20, right: 0, left: 20, bottom: 20 }}
              onMouseOver={(e) => CloseSelectedInsight(node)}
              onMouseOut={(e) => OpenSelectedInsight(node, !state.tooltip.show)}
            >
              {!state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div
                              className="standard-tooltip"
                              style={{ width: "300px" }}
                            >
                              {tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {Object.keys(payload)
                                  .sort(
                                    (a, b) =>
                                      payload[b].value - payload[a].value
                                  )
                                  .map((key, index) => {
                                    return (
                                      <div
                                        className="standard-tooltip__item"
                                        key={key}
                                      >
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[key].color,
                                          }}
                                        ></span>
                                        <span
                                          className="standard-tooltip__key"
                                          style={{
                                            color: payload[key].color,
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {metricNames[payload[key].dataKey] ||
                                            formatMetricKey(
                                              payload[key].dataKey
                                            )}{" "}
                                        </span>
                                        <span className="standard-tooltip__value">
                                          {selectFormat(
                                            payload[key].name
                                              .toLocaleLowerCase()
                                              .includes("rate")
                                              ? "percentage"
                                              : "number",
                                            payload[key].value
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                {widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}

              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />
              {renderCharts(preparedData)}
              <XAxis dataKey="date" tickLine={false} axisLine={false} />
              {allKeys.filter((m) => m.toLocaleLowerCase().includes("rate"))
                .length ? (
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  mirror={true}
                  tick={<CustomizedYAxisTickRightPercent />}
                />
              ) : (
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  mirror={true}
                  tick={<CustomizedYAxisTick />}
                />
              )}
              {allKeys.filter((m) => m.toLocaleLowerCase().includes("rate"))
                .length
                ? [
                    <YAxis
                      key={1}
                      orientation={"right"}
                      yAxisId={"right"}
                      axisLine={false}
                      tickLine={false}
                      mirror={true}
                      tick={<CustomizedYAxisTickRightPercent />}
                    />,
                  ]
                : [
                    <YAxis
                      key={1}
                      orientation={"right"}
                      yAxisId={"right"}
                      axisLine={false}
                      tickLine={false}
                      mirror={true}
                      tick={<CustomizedYAxisTickRight />}
                    />,
                  ]}

              {/* Adds insight icon under x-axis ticks  */}
              {insightsData &&
                widgetsInsights.map((o, index) => (
                  <ReferenceDot
                    x={o.date}
                    y={0}
                    key={title + index}
                    ifOverflow="extendDomain"
                    shape={
                      <InsightsTickIndicator
                        insights={widgetsInsights}
                        date={o.date}
                        node={node}
                      />
                    }
                  />
                ))}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={node}>
            {widgetsInsights.length > 0 && !state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
        <div
          className="xray-tooltip-container"
          style={{
            top: "5.4rem",
            left:
              state.tooltip.x + state.tooltip.width + 220 < state.width
                ? state.tooltip.x + state.tooltip.width + 15 + "px"
                : state.tooltip.x -
                  getXrayTooltipPosition(divPosition.current, state.tooltip) +
                  "px",
            opacity: state.tooltip.show ? 1.0 : 0.0,
            visibility: state.tooltip.show ? "visible" : "hidden",
          }}
        >
          <XRayTooltip
            data={state.tooltip}
            insights={widgetsInsights}
            close={closeTooltip.bind(this)}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default LeeDailyTrendCampaignStackedBars;
